import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { CustomMembershipFormSchemaValues } from 'src/features/membership/formSchema';
import { membershipMessages } from 'src/features/membership/utils/messages';
import InputFormField from 'src/registry/components/forms/InputFormField';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalMessages } from 'src/registry/utils/messages';
import { Separator } from 'src/registry/ui/Separator';
import EntityFormField from 'src/features/membership/components/forms/EntityFormField';
import { useListSites } from 'src/features/sites/api/useListSites';
import { Checkbox } from 'src/registry/ui/Checkbox';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { useListPricingPlans } from 'src/features/pricingPlan/api/useListPricingPlans';
import MultiSelectFormField from 'src/registry/components/forms/MultiSelectFormField';
import { useListRoles } from 'src/features/role/api/useListRoles';
import { useParking } from 'src/providers/ParkingProvider';

type CustomMembershipFormProps = {
    form: UseFormReturn<CustomMembershipFormSchemaValues>;
    onSubmit: (values: CustomMembershipFormSchemaValues) => void;
};
function CustomMembershipForm({ onSubmit, form }: CustomMembershipFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: sitesData } = useListSites({ parkingId });
    const { data: pricingPlansData } = useListPricingPlans({ parkingId });
    const { data: rolesData } = useListRoles({ parkingId });

    const [
        has_pricing_plan,
        allow_extra_visitors,
        limit_spaces,
    ] = form.watch(['has_pricing_plan', 'allow_extra_visitors', 'limit_spaces']);

    const siteOptions = React.useMemo(
        () => {
            const filteredEmptySiteTranslations = sitesData?.results?.filter((site) => site.translations.length > 0) ?? []

            return filteredEmptySiteTranslations?.map((site) => {
                const translatedSite = site.translations.find((s) => s.language === locale);

                return { value: site.id, label: translatedSite?.name };
            })
        },
         [sitesData, locale],
    );

    const pricingPlanOptions = React.useMemo(
        () => {
            const filteredEmptyPricingPlanTranslations = pricingPlansData?.results?.filter((plan) => plan.translations.length > 0) ?? [];

            return filteredEmptyPricingPlanTranslations?.map((plan) => {
                const translatedPlan = plan.translations.find((p) => p.language === locale);

                return { value: plan.identity, label: translatedPlan?.name };
            });
        },
        [pricingPlansData, locale],
    );

    const CheckboxFormField = ({
        fieldName,
        title,
        resetFieldNames,
        disabled = false,
        }: {
        fieldName: keyof CustomMembershipFormSchemaValues;
        title: { id: string; defaultMessage: string };
        disabled?: boolean;
        resetFieldNames?: string[];
    }) => {
        return (
            <FormField
                control={form.control}
                name={fieldName}
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...title} />
                        </FormLabel>
                        <FormControl>
                            <Checkbox
                                disabled={disabled}
                                checked={!!field.value}
                                onCheckedChange={(checked) => {
                                    const value = checked === true;
                                    field.onChange(value);

                                    if (!value) {
                                        resetFieldNames?.map((field) => {
                                            const fieldSetValue = field === 'duration' ? undefined : false;
                                            form.setValue(
                                                field as keyof CustomMembershipFormSchemaValues,
                                                fieldSetValue,
                                                {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                },
                                            );
                                        });
                                    }
                                }}
                            />
                        </FormControl>
                    </FormItem>
                )}
            />
        );
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<CustomMembershipFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(generalMessages.name)}
            />
            <FormField
                control={form.control}
                name="site"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...membershipMessages.site} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(membershipMessages.site)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {siteOptions?.map((option) => (
                                            <SelectItem key={option?.value} value={option?.value}>
                                                {option?.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <Separator />
            <EntityFormField form={form} />
            <Separator />
            <CheckboxFormField
                fieldName="has_pricing_plan"
                title={membershipTypeMessages.hasPricingPlan}
                resetFieldNames={['apply_pricing_per_space', 'auto_renew', 'pricing_plan_identity']}
            />
            <FormField
                control={form.control}
                name="pricing_plan_identity"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required={!!has_pricing_plan}>
                            <FormattedMessage {...membershipMessages.pricingPlan} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={field.onChange}
                                    value={field.value ?? ''}
                                    disabled={!has_pricing_plan}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(membershipMessages.pricingPlan)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {pricingPlanOptions?.map((option) => (
                                            <SelectItem key={option?.value} value={option?.value}>
                                                {option?.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <CheckboxFormField
                fieldName="apply_pricing_per_space"
                title={membershipTypeMessages.applyPricingPerSpace}
                disabled={!has_pricing_plan}
            />

            <CheckboxFormField
                fieldName="auto_renew"
                title={membershipTypeMessages.autoRenew}
                disabled={!has_pricing_plan}
            />
            <Separator />

            <CheckboxFormField
                fieldName="limit_duration"
                title={membershipTypeMessages.limitDuration}
                resetFieldNames={['custom_duration', 'duration', 'duration_unit']}
            />
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(membershipMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime dateISO={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(membershipMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime dateISO={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <Separator />

            <InputFormField<CustomMembershipFormSchemaValues>
                form={form}
                name="max_allowed_spaces_count"
                label={formatMessage(membershipMessages.maxAllowedSpaces)}
                type="number"
            />

            <CheckboxFormField
                fieldName="limit_spaces"
                title={membershipTypeMessages.limitSpaces}
                resetFieldNames={['allow_extra_visitors', 'max_extra_visitors_count']}
            />

            <CheckboxFormField
                fieldName="allow_extra_visitors"
                title={membershipTypeMessages.extraVisitors}
                disabled={!limit_spaces}
            />

            <InputFormField<CustomMembershipFormSchemaValues>
                form={form}
                name="max_extra_visitors_count"
                type="number"
                required={!!allow_extra_visitors}
                disabled={!allow_extra_visitors}
                label={formatMessage(membershipTypeMessages.maxExtraVisitors)}
            />

            <Separator />

            <CheckboxFormField
                fieldName="allow_access_over_capacity_limit"
                title={membershipTypeMessages.allowAccessOverCapacityLimit}
            />

            <CheckboxFormField
                fieldName="allow_access_as_client_outside_restrictions"
                title={membershipTypeMessages.allowAccessAsClientOutsideRestrictions}
            />

            <CheckboxFormField
                fieldName="contribute_to_available_spaces"
                title={membershipTypeMessages.contributeToAvailableSpaces}
            />

            <MultiSelectFormField<CustomMembershipFormSchemaValues>
                form={form}
                name="limit_to_roles"
                required={false}
                label={formatMessage(membershipTypeMessages.limitToRoles)}
                options={rolesData?.results}
            />
        </Form>
    );
}

export default CustomMembershipForm;
